import { GifsResult } from '@giphy/js-fetch-api'
import { ForwardedRef, ReactNode, forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Dimensions, Position } from 'types'
import PortalTooltipContext from 'ui/src/components/controls/tooltip/portal-tooltip/context'
import { ArtboardRef } from '../artboard-gl/types'
import Artboard from './artboard'
import ExportArtboard from './artboard/export-board'
import Editor from './editors'
import ContextManager from './manager'
import Navigation, { CloseButton } from './navigation'
import Overlay from './overlays'
import { Container, Content, Panel } from './style'
import { UGEEditor, UGEGAEventCallback, UGELayer, UGELayerActionLabels, UGERef } from './types'

export type UGEProps = {
    apiKey: string
    children?: ReactNode
    className?: string
    closeable?: boolean
    defaultEditor?: UGEEditor
    editorHeader?: ReactNode
    enableStickers?: boolean
    allowedEditors?: UGEEditor[]
    useSeparateExportRenderer?: boolean
    artboardDesynchronized?: boolean
    persistArtboardControls?: boolean
    showArtboardControls?: boolean
    initialDimensions?: Dimensions
    layerActionLabels?: UGELayerActionLabels
    maxPreviewWidth?: number
    showEditor?: boolean
    source: File | string
    fetchFavoriteStickers?: (offset: number) => Promise<GifsResult>
    onClose?: () => void
    onArtboardMouseEnter?: () => void
    onArtboardMouseLeave?: () => void
    onArtboardPointerDown?: () => void
    onGAEvent: UGEGAEventCallback
    onRenderError?: (error: Error) => void
}

/**
 Universal GIPHY Editor

██╗   ██╗ ██████╗ ███████╗
██║   ██║██╔════╝ ██╔════╝
██║   ██║██║  ███╗█████╗  
██║   ██║██║   ██║██╔══╝  
╚██████╔╝╚██████╔╝███████╗
 ╚═════╝  ╚═════╝ ╚══════╝
 */
const UGE = (
    {
        apiKey,
        children,
        className,
        closeable = false,
        defaultEditor,
        editorHeader,
        enableStickers = false,
        allowedEditors,
        useSeparateExportRenderer = false,
        artboardDesynchronized,
        persistArtboardControls = true,
        showArtboardControls = true,
        initialDimensions,
        layerActionLabels,
        maxPreviewWidth,
        showEditor = true,
        source,
        fetchFavoriteStickers,
        onClose,
        onArtboardMouseEnter,
        onArtboardMouseLeave,
        onArtboardPointerDown,
        onGAEvent,
        onRenderError,
    }: UGEProps,
    ref: ForwardedRef<UGERef>
) => {
    const artboardRef = useRef<ArtboardRef>(null)
    const exportArtboardRef = useRef<ArtboardRef>(null)
    const [crop, setCrop] = useState<Position | undefined>()
    const [dimensions, setDimensions] = useState<Dimensions | null>(null)
    const [gifIds, setGifIds] = useState<string[]>([])
    const [stickerLayer, setStickerLayer] = useState<UGELayer>()

    function getExportArtboard() {
        return useSeparateExportRenderer ? exportArtboardRef.current || artboardRef.current : artboardRef.current
    }

    useImperativeHandle(ref, () => ({
        createGIF: async () => {
            const targetArtboard = getExportArtboard()

            if (targetArtboard) {
                return targetArtboard.createGIF(crop)
            }

            return null
        },
        createSprite: async () => {
            const targetArtboard = getExportArtboard()

            if (targetArtboard) {
                return targetArtboard.createSprite(crop)
            }

            return null
        },
        getDimensions: () => {
            return dimensions
        },
        getGifIds: () => {
            return gifIds
        },
        getStickerLayer: () => {
            return stickerLayer
        },
    }))

    return (
        <ContextManager
            apiKey={apiKey}
            artboardRef={artboardRef}
            crop={crop}
            defaultEditor={defaultEditor}
            enableStickers={enableStickers}
            allowedEditors={allowedEditors}
            persistArtboardControls={persistArtboardControls}
            showArtboardControls={showArtboardControls}
            initialDimensions={initialDimensions}
            layerActionLabels={layerActionLabels}
            maxPreviewWidth={maxPreviewWidth}
            source={source}
            fetchFavoriteStickers={fetchFavoriteStickers}
            setCrop={setCrop}
            setDimensions={setDimensions}
            setGifIds={setGifIds}
            setStickerLayer={setStickerLayer}
            trackGAEvent={onGAEvent}
        >
            <PortalTooltipContext>
                <Container className={className}>
                    {closeable && <CloseButton onClick={onClose} />}
                    <Panel $width={maxPreviewWidth}>
                        <Content>
                            <Artboard
                                desynchronized={artboardDesynchronized}
                                ref={artboardRef}
                                onMouseEnter={onArtboardMouseEnter}
                                onMouseLeave={onArtboardMouseLeave}
                                onPointerDown={onArtboardPointerDown}
                                onRenderError={onRenderError}
                            >
                                <Overlay />
                            </Artboard>
                            {useSeparateExportRenderer && <ExportArtboard ref={exportArtboardRef} />}
                            {showEditor && editorHeader}
                            {showEditor && <Editor />}
                        </Content>
                        {children}
                    </Panel>
                    <Navigation />
                </Container>
            </PortalTooltipContext>
        </ContextManager>
    )
}

export default forwardRef(UGE)
